import React, { useEffect, useState } from "react";
import TableHead from "@/components/tables/TableHead";
import TemplateTable from "@/components/tables/TemplateTable";
import { fetchEnvos } from "@/services/envelope.service";
import { fetchTemplates } from "@/services/template.service";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { getListCacheBootstrap, writeListCache } from "@/utils/listPageCache";

type Props = {};

function TemplateList({}: Props) {
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting, filter, setFilter, searchItem, setSearchItem, resetDataQueries] = useDataQueries();
    const listBootstrap = getListCacheBootstrap("templates", { dataLength, sorting, searchItem, filter });

    const [allFetched, setAllFetched] = useState(false);
    const [loadData, setLoadData] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);
    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [templates, setTemplates] = useState<any>(listBootstrap.items);
    const [templatesLeft, setTemplatesLeft] = useState(true);
    const [templateHistory, setTemplateHistory] = useState<any>(listBootstrap.items);

    const handleFetchAllTemplates = () => {
        fetchTemplates(
            pageNo,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                setLoadData(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (response.data?.templates?.length < dataLength) {
                    setTemplatesLeft(false);
                    setAllFetched(true);
                }
                const templates = response.data.templates;
                setTemplates(templates);
                setTemplateHistory(templates);
            },
            (error: any) => {
                console.log(error);
                setLoadData(false);
                setIsLoading(false);
            }
        );
    };
    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("templates", { dataLength, sorting, searchItem, filter });

        setPageNo(1);
        setTemplatesLeft(true);
        setAllFetched(false);
        if (cached) {
            setTemplates(cached.items);
            setTemplateHistory(cached.items);
            setTotalCount(cached.totalCount);
            setLoadData(false);
            setIsLoading(false);
        } else if (!cached) {
            setIsLoading(true);
            setLoadData(true);
        }

        fetchTemplates(
            1,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                try {
                    const nextTemplates = response?.data?.templates ?? [];
                    writeListCache(cacheKey, nextTemplates, response?.data?.total_count ?? 0);
                    setTotalCount(response?.data?.total_count ?? 0);
                    if (nextTemplates.length < dataLength) {
                        setTemplatesLeft(false);
                        setAllFetched(true);
                    }
                    setTemplates(nextTemplates);
                    setTemplateHistory(nextTemplates);
                } finally {
                    setLoadData(false);
                    setIsLoading(false);
                }
            },
            () => {
                setLoadData(false);
                setIsLoading(false);
            }
        );
    }, [dataLength, sorting, searchItem]);

    window.onscroll = function () {
        if (allFetched || isLoading) {
            return;
        }
        if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1) {
            setPageNo(pageNo + 1);
            setIsLoading(true);
            if (templatesLeft) {
                fetchTemplates(
                    pageNo + 1,
                    dataLength,
                    sorting,
                    filter,
                    searchItem,
                    (response: any) => {
                        setIsLoading(false);
                        setTotalCount(response.data.total_count);
                        if (response.data?.templates?.length < dataLength) {
                            setTemplatesLeft(false);
                            setAllFetched(true);
                        }
                        const sorted_templates = response.data.templates;
                        const result = [...templates, ...sorted_templates];
                        setTemplates(result);
                        setTemplateHistory(result);
                    },
                    (error: any) => {
                        console.log(error);
                        setIsLoading(false);
                    }
                );
            }
        }
    };

    const search = (searchText: string) => {
        setSearchItem(searchText);
    };

    const resetResponse = () => {
        setTemplatesLeft(true);
        setAllFetched(false);
    };

    const reset = () => {
        resetDataQueries?.();
        resetResponse();
    };
    const handleApplyFilter = () => {
        setPageNo(1);
        resetResponse();
        setLoadData(true);
        setIsLoading(true);
        fetchTemplates(
            1,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                setLoadData(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (response.data?.templates?.length < dataLength) {
                    setTemplatesLeft(false);
                    setAllFetched(true);
                }
                const templates = response.data.templates;
                setTemplates(templates);
                setTemplateHistory(templates);
            },
            (error: any) => {
                console.log(error);
                setLoadData(false);
                setIsLoading(false);
            }
        );
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead id="envelope-table" handleReset={resetResponse} pageNo={pageNo as number} dataLength={dataLength as number} setFilter={setFilter} filter={filter} setDataLength={setDataLength as (dataLength: number) => void} reset={reset} search={search} title="Templates" currentEnvo={templates.length} totalEnvo={totalCount} handleApplyFilter={handleApplyFilter} />
            </div>
            <TemplateTable id="template-table" reset={reset} title="Templates" setTemplates={setTemplates} search={search} isLoading={isLoading} dataLength={dataLength as number} sorting={sorting} setSorting={setSorting as (sorting: any) => void} searchItem={searchItem as string} allFetched={allFetched} templates={templates} dataLoad={loadData} resetDataQueries={resetDataQueries} setPageNo={setPageNo} loadingData={isLoading} />
        </div>
    );
}

export default TemplateList;
